// Human-readable, per-locale labels for the ArmProgress app's EquipmentType
// union (lib/data/exercises.ts in the sibling ArmProgress repo). Hand-authored
// here rather than pulled from ArmProgress's own locale files, since the app
// has no standalone "equipment name" translation namespace to sync from —
// only these short, stable terms, safe to hand-maintain directly.
import type { Lang } from './data.generated';

export type EquipmentKey =
  | 'dumbbell'
  | 'barbell'
  | 'cable'
  | 'band'
  | 'sledgehammer'
  | 'gripper'
  | 'strap'
  | 'table'
  | 'wristRoller'
  | 'riceBucket'
  | 'riser'
  | 'kettlebell'
  | 'chain'
  | 'multispinner'
  | 'ezBar'
  | 'pullupBar'
  | 'none';

const LABELS: Record<Lang, Record<EquipmentKey, string>> = {
  en: {
    dumbbell: 'Dumbbell', barbell: 'Barbell', cable: 'Cable machine', band: 'Resistance band',
    sledgehammer: 'Sledgehammer', gripper: 'Hand gripper', strap: 'Arm-wrestling strap',
    table: 'Arm-wrestling table', wristRoller: 'Wrist roller', riceBucket: 'Rice bucket',
    riser: 'Riser tool', kettlebell: 'Kettlebell', chain: 'Chain', multispinner: 'Multi-spinner',
    ezBar: 'EZ bar', pullupBar: 'Pull-up bar', none: 'No equipment',
  },
  ru: {
    dumbbell: 'Гантель', barbell: 'Штанга', cable: 'Блочный тренажёр', band: 'Резиновый эспандер',
    sledgehammer: 'Кувалда', gripper: 'Кистевой эспандер', strap: 'Ремень для армрестлинга',
    table: 'Стол для армрестлинга', wristRoller: 'Ролик для запястий', riceBucket: 'Ведро с рисом',
    riser: 'Райзер', kettlebell: 'Гиря', chain: 'Цепь', multispinner: 'Мультиспиннер',
    ezBar: 'EZ-гриф', pullupBar: 'Турник', none: 'Без инвентаря',
  },
  de: {
    dumbbell: 'Kurzhantel', barbell: 'Langhantel', cable: 'Kabelzug', band: 'Widerstandsband',
    sledgehammer: 'Vorschlaghammer', gripper: 'Handtrainer', strap: 'Armwrestling-Gurt',
    table: 'Armwrestling-Tisch', wristRoller: 'Handgelenkroller', riceBucket: 'Reiseimer',
    riser: 'Riser-Tool', kettlebell: 'Kettlebell', chain: 'Kette', multispinner: 'Multispinner',
    ezBar: 'SZ-Stange', pullupBar: 'Klimmzugstange', none: 'Kein Gerät',
  },
  fr: {
    dumbbell: 'Haltère', barbell: 'Barre', cable: 'Poulie', band: 'Bande élastique',
    sledgehammer: 'Masse', gripper: 'Pince de préhension', strap: 'Sangle d’armwrestling',
    table: 'Table d’armwrestling', wristRoller: 'Rouleau de poignet', riceBucket: 'Seau de riz',
    riser: 'Outil riser', kettlebell: 'Kettlebell', chain: 'Chaîne', multispinner: 'Multi-spinner',
    ezBar: 'Barre EZ', pullupBar: 'Barre de traction', none: 'Aucun équipement',
  },
  es: {
    dumbbell: 'Mancuerna', barbell: 'Barra', cable: 'Polea', band: 'Banda elástica',
    sledgehammer: 'Almádena', gripper: 'Ejercitador de mano', strap: 'Correa de pulseo',
    table: 'Mesa de pulseo', wristRoller: 'Rodillo de muñeca', riceBucket: 'Cubo de arroz',
    riser: 'Herramienta riser', kettlebell: 'Kettlebell', chain: 'Cadena', multispinner: 'Multispinner',
    ezBar: 'Barra Z', pullupBar: 'Barra de dominadas', none: 'Sin equipo',
  },
  'pt-BR': {
    dumbbell: 'Halter', barbell: 'Barra', cable: 'Cross-over', band: 'Faixa elástica',
    sledgehammer: 'Marreta', gripper: 'Alicate de mão', strap: 'Cinta de queda de braço',
    table: 'Mesa de queda de braço', wristRoller: 'Rolo de pulso', riceBucket: 'Balde de arroz',
    riser: 'Ferramenta riser', kettlebell: 'Kettlebell', chain: 'Corrente', multispinner: 'Multispinner',
    ezBar: 'Barra EZ', pullupBar: 'Barra fixa', none: 'Sem equipamento',
  },
  ja: {
    dumbbell: 'ダンベル', barbell: 'バーベル', cable: 'ケーブルマシン', band: 'レジスタンスバンド',
    sledgehammer: 'スレッジハンマー', gripper: 'ハンドグリッパー', strap: 'アームレスリング用ストラップ',
    table: 'アームレスリング用テーブル', wristRoller: 'リストローラー', riceBucket: 'ライスバケツ',
    riser: 'ライザーツール', kettlebell: 'ケトルベル', chain: 'チェーン', multispinner: 'マルチスピナー',
    ezBar: 'EZバー', pullupBar: '懸垂バー', none: '器具なし',
  },
  ko: {
    dumbbell: '덤벨', barbell: '바벨', cable: '케이블 머신', band: '저항 밴드',
    sledgehammer: '슬레지해머', gripper: '손 그립퍼', strap: '암레슬링 스트랩',
    table: '암레슬링 테이블', wristRoller: '손목 롤러', riceBucket: '쌀통',
    riser: '라이저 도구', kettlebell: '케틀벨', chain: '체인', multispinner: '멀티스피너',
    ezBar: 'EZ바', pullupBar: '풀업 바', none: '기구 없음',
  },
};

export function equipmentLabel(key: string, lang: Lang): string {
  const table = LABELS[lang] ?? LABELS.en;
  return table[key as EquipmentKey] ?? LABELS.en[key as EquipmentKey] ?? key;
}
